<template>
    <div class="direct-messages-widget">
        <div class="direct-messages-overlay" v-if="expanded" @click="minimizeDirectMessages()">
        </div>
        <div class="direct-messages-content" v-if="expanded">
            <div class="direct-messages-title" v-if="expanded" @click="minimizeDirectMessages()">
                <span class="title-text">
                    Direct Messages
                </span>
                <span class="toggle-wrapper">
                    <fa-icon class="toggle-icon collapse-icon" icon="chevron-down" />
                </span>
            </div>
            <div class="direct-messages-inner">
                <div class="conversations">
                    <div class="conversations-content">
                        <div class="search-bar">
                            <div class="search-form">
                                <input
                                    class="search-input"
                                    name="search"
                                    placeholder="Find a player"
                                    v-model="search.username"
                                    @input="onSearch(search.username)"
                                    autocomplete="off"
                                />
                                <span class="search-icon">
                                    <fa-icon icon="search" />
                                </span>
                                <div class="search-results" v-if="searchResults.length && search.username !== ''">
                                    <div class="search-result"
                                        v-for="searchResult in searchResults"
                                        :key="`search-result-${searchResult.id}`"
                                        @click="startConversation(searchResult.id, searchResult.username)"
                                    >
                                        {{searchResult.username}}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="conversations-list" ref="conversation-list">
                            <conversation-card
                                v-for="conversation in unblockedConversations"
                                :key="`conversation-${conversation.id}`"
                                :activeConversation="activeConversation"
                                :conversation="conversation"
                                :onClick="loadConversation"
                                :onArchive="archiveConversation"
                                :onBlock="blockConversation"
                                :currentUserId="userId"
                                :blocked="false"
                                :unreadMessages="unreadMessages(conversation)"
                            />
                            <div class="blocked-conversations-title" v-if="blockedConversations.length">
                                <span class="title-line"></span>
                                <span class="title-text">Blocked</span>
                                <span class="title-line"></span>
                            </div>
                            <conversation-card
                                v-for="conversation in blockedConversations"
                                :key="`conversation-${conversation.id}`"
                                :activeConversation="activeConversation"
                                :conversation="conversation"
                                :onClick="loadConversation"
                                :onArchive="archiveConversation"
                                :onUnblock="unblockConversation"
                                :currentUserId="userId"
                                :blocked="true"
                                :unreadMessages="unreadMessages(conversation)"
                            />
                        </div>
                    </div>
                </div>
                <div class="chat-room">
                    <div class="messages" ref="messages">
                        <div class="message-wrapper" v-for="message in messages" :key="`message-${message.id}`">
                            <span class="username">
                                <span
                                    class="label"
                                    :class="{ [getLabelColor(message.user.username)]: true }"
                                >
                                    {{ message.user.username }}
                                </span>:
                            </span>
                            <span class="message" v-html="message.message">
                            </span>
                        </div>
                    </div>
                    <form class="chat-form" v-on:submit.prevent="onSubmit">
                        <textarea
                            ref="chat-input"
                            class="form-control chat-input"
                            v-model="message"
                            v-on:keypress.enter="onEnter"
                            placeholder="Send a message"
                            name="message"
                            autocomplete="off"
                            :rows="textareaRows"
                            @input="onInputChange"
                        >
                        </textarea>
                        <button
                            class="btn btn-primary submit-btn"
                            :class="{
                                ['btn-disabled']: !activeConversation,
                            }"
                            :disabled="!activeConversation"
                            @click="onEnter"
                            v-if="message.length"
                        >
                            Send
                        </button>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import shuffle from 'lodash.shuffle';
import api from '../../helpers/api';
import { getUsers } from '../../helpers/general';
import { chatColors } from '../Chat/Chat-types';
import { getUnreadMessages } from './DirectMessagesWidget-helpers';

export default {
    name: 'DirectMessagesWidget',
    mounted() {
        if (this.userId) {
            this.getConversations();
        }
    },
    watch: {
        'search.username': function watchSearch(current, prev) {
            if (prev !== '' && current === '') {
                this.searchResults = [];
            }
        },
        expanded: async function watchExpanded(current, prev) {
            if (!prev && current) {
                this.colors = shuffle(chatColors);
                this.getConversations();
            }

            if (prev && !current) {
                this.$store.dispatch('setActiveConversation', null);
            }
        },
        conversations: async function watchConversations(current, prev) {
            if (this.activeConversation && JSON.stringify(prev) !== JSON.stringify(current)) {
                this.scrollToBottom();
            }
        },
        userId: function getConversationsOnLogin(current, prev) {
            if (!prev && current) {
                this.getConversations();
            }
        },
    },
    data() {
        return {
            colors: shuffle(chatColors),
            loaded: false,
            message: '',
            textareaRows: 1,
            searchResults: [],
            search: {
                username: '',
            },
            showConversationMenu: false,
            inputFocused: false,
        };
    },
    computed: {
        activeConversation() {
            return this.$store.state.directMessagesState.activeConversation;
        },
        blockedConversations() {
            return this.conversations.filter(conversation =>
                conversation.users.find(user =>
                    user.id === this.userId,
                ).ConversationUserResource.blocked,
            );
        },
        chatInputRef() {
            return this.$refs['chat-input'];
        },
        converastionListRef() {
            return this.$refs['conversation-list'];
        },
        conversations() {
            return this.$store.state.directMessagesState.conversations;
        },
        expanded() {
            return this.$store.state.directMessagesState.expanded;
        },
        messages() {
            return (this.activeConversation && this.conversations.find(conversation =>
                conversation.id === this.activeConversation.id,
            )?.messages) || [];
        },
        unblockedConversations() {
            return this.conversations.filter(conversation =>
                !conversation.users.find(user =>
                    user.id === this.userId,
                ).ConversationUserResource.blocked,
            );
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        username() {
            return this.$store.state.loginState.username;
        },
    },
    methods: {
        scrollToBottom() {
            setTimeout(() => {
                if (this.$refs.messages) {
                    this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
                }
            }, 0);
        },
        async getConversations() {
            if (this.userId) {
                await this.$store.dispatch('getConversations');
            }
        },
        getLabelColor(username) {
            return this.colors[this.activeConversation && this.activeConversation
                .users
                .findIndex(user => user.username === username)];
        },
        toggleConversationMenu(event) {
            event.stopPropagation();
            this.showConversationMenu = !this.showConversationMenu;
        },
        markConversationAsReadLocally(conversationId) {
            this.$store.dispatch('markConversationAsReadLocally', {
                conversationId,
                currentUserId: this.userId,
            });
        },
        minimizeDirectMessages() {
            this.$store.dispatch('setExpandedDirectMessages', false);
        },
        async onSubmit() {
            const {
                message,
                activeConversation,
            } = this;

            if (message.trim()) {
                const options = {
                    message,
                    conversationId: activeConversation?.id,
                    recipientId: activeConversation?.users.find(user => user.id !== this.userId)?.id,
                };

                this.message = '';
                this.textareaRows = 1;

                const response = await this.$store.dispatch('sendDirectMessage', options);

                if (response.ok) {
                    const updatedConverastion = await response.json();
                    await this.$store.dispatch('updateConversation', updatedConverastion);
                    this.scrollToBottom();
                } else {
                    const error = await response.json();

                    this.$notify({
                        group: 'app',
                        clean: true,
                    });

                    this.$notify({
                        group: 'app',
                        title: 'Error',
                        type: 'error',
                        text: error.message,
                    });
                }
            }
        },
        onEnter(event) {
            if (event.shiftKey) {
                return;
            }

            event.preventDefault();

            if (!this.activeConversation) {
                this.$notify({
                    group: 'app',
                    clean: true,
                });

                this.$notify({
                    group: 'app',
                    type: 'error',
                    text: 'Please select a user before trying to send a message',
                });
                return;
            }

            this.onSubmit();
        },
        onInputChange(event) {
            const message = event.target.value;
            this.message = message;
            let chars = message.length;
            const charsPerRow = 24;
            const lineBreaks = message.match(/\r|\n/) || [];

            if (lineBreaks.length) {
                chars += lineBreaks.length * charsPerRow;
            }

            if (chars < charsPerRow) {
                this.textareaRows = 1;
            } else if (chars >= charsPerRow && chars <= charsPerRow * 2) {
                this.textareaRows = 2;
            } else if (chars >= charsPerRow * 2 && chars <= charsPerRow * 3) {
                this.textareaRows = 3;
            } else {
                this.textareaRows = 4;
            }
        },
        onSearch: debounce(async function onSearch(username) {
            const page = 1;
            this.page = page;
            this.loaded = false;
            this.loading = true;
            const response = await getUsers({ username, page });

            if (response.ok) {
                this.searchResults = (await response.json()).users;
            }

            this.loaded = true;
            this.loading = false;
        }, 550),
        async archiveConversation(conversationId) {
            await api.request(`/conversation/${conversationId}/archive`, 'POST');
            await this.$store.dispatch('getConversations');
        },
        async blockConversation(conversationId) {
            await api.request(`/conversation/${conversationId}/block`, 'POST');
            await this.$store.dispatch('getConversations');
        },
        async unarchiveConversation(conversationId, autoSelect) {
            await api.request(`/conversation/${conversationId}/unarchive`, 'POST');
            await this.$store.dispatch('getConversations');

            if (autoSelect) {
                this.$store.dispatch('setActiveConversation', this.conversations.find(conversation =>
                    conversation.id === conversationId,
                ));
            }
        },
        async unblockConversation(conversationId) {
            await api.request(`/conversation/${conversationId}/unblock`, 'POST');
            await this.$store.dispatch('getConversations');
        },
        async startConversation(recipientId) {
            this.searchResults = [];
            this.search.username = '';
            let success = true;
            let converastionId;

            const existingConversation = this.conversations.find(conversation =>
                conversation.users.find(user => user.id === recipientId),
            );

            this.colors = shuffle(chatColors);

            if (existingConversation) {
                converastionId = existingConversation.id;

                if (existingConversation.users.find(user => user.id === this.userId).ConversationUserResource.archived) {
                    await this.unarchiveConversation(existingConversation.id, true);
                } else {
                    this.$store.dispatch('setActiveConversation', existingConversation);
                }
            } else {
                const response = await api.request(`/conversation/${recipientId}`, 'POST');

                if (response.ok) {
                    const conversation = await response.json();

                    converastionId = conversation.id;

                    this.$store.dispatch('addConversation', conversation);
                    this.$store.dispatch('setActiveConversation', conversation);
                } else {
                    success = false;

                    const error = await response.json();

                    this.$notify({
                        group: 'app',
                        clean: true,
                    });

                    this.$notify({
                        group: 'app',
                        type: 'error',
                        text: error.message,
                    });
                }
            }

            if (success) {
                const targetConverastion = document.querySelector(`[data-conversation-id="${converastionId}"]`);

                if (targetConverastion) {
                    const scrollTop = targetConverastion.offsetTop - 8;
                    this.$refs['conversation-list'].scrollTop = scrollTop > 0 ? scrollTop : 0;
                }

                this.$refs['chat-input'].focus();
                this.scrollToBottom();
            }
        },
        async loadConversation(conversationId) {
            if (this.activeConversation?.id === conversationId) {
                this.$store.dispatch('setActiveConversation', null);
            } else {
                this.colors = shuffle(chatColors);
                this.$store.dispatch('setActiveConversation', this.conversations.find(conversation =>
                    conversation.id === conversationId,
                ));
                this.$refs['chat-input'].focus();
                this.scrollToBottom();
                await this.$store.dispatch('markConversationAsRead', conversationId);
                await this.getConversations();
            }
        },
        unreadMessages(conversation) {
            return getUnreadMessages(conversation, this.userId, this.username);
        },
    },
    sockets: {
        async onUpdateConversations() {
            if (this.userId) {

                if (this.activeConversation) {
                    this.markConversationAsReadLocally(this.activeConversation.id);
                    await this.$store.dispatch('markConversationAsRead', this.activeConversation.id);
                }

                this.getConversations();
            }
        },
    },
};
</script>

<style scoped lang="scss">
@use "@/styles/styles.scss" as *;

.direct-messages-widget {
    font-family: 'Avenir', Helvetica, Arial, 'RobotoSlab', sans-serif;
    font-size: .85rem;
    position: relative;
    z-index: 200;

    .direct-messages-content {
        background: white;
        border-radius: 4px;
        bottom: 0;
        box-shadow: 0px 0px 3px 1px rgba(123,125,125,0.5);
        position: absolute;
        right: 0;
        min-height: 342.5px;
        display: flex;
        flex-direction: column;
        z-index: 201;

        .direct-messages-title {
            text-transform: uppercase;
            font-weight: bold;
            line-height: 1.5em;
            vertical-align: middle;
            padding: 8px 6px 10px;
            color: $dark-brown;
            position: relative;
            padding-right: 20px;
            cursor: pointer;

            .title-text {
                font-family: 'IrishUncialfabeta';
                font-size: 1.3em;
                vertical-align: middle;
            }

            .toggle-wrapper {
                cursor: pointer;
                position: absolute;
                right: 15px;
                top: 50%;
                transform: translateY(-50%);

                .toggle-icon {
                    vertical-align: middle;
                    font-size: 1.1em;

                    &.expand-icon {
                        margin-bottom: 1px;
                    }
                }
            }
        }

        .direct-messages-inner {
            display: flex;
            flex-grow: 1;

            .conversations {
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                min-width: 135px;
                padding: 0 0 12px 12px;

                .conversations-content {
                    background: $extra-light-gray;
                    border-radius: 4px;
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;

                    .search-form {
                        position: relative;

                        .search-input {
                            border: none;
                            border-top-left-radius: 4px;
                            border-top-right-radius: 4px;
                            padding: 6px 4px;
                            width: 100%;
                            border-bottom: 2px solid $light-gray;
                            box-sizing: border-box;
                        }

                        .search-icon {
                            position: absolute;
                            right: 5px;
                            top: 50%;
                            transform: translateY(-50%);
                            font-size: .8rem;
                            color: $gray;
                        }

                        .search-results {
                            overflow: auto;
                            max-height: 150px;
                            position: absolute;
                            left: 0;
                            top: 100%;
                            z-index: 1;
                            background: white;
                            width: 100%;
                            text-align: left;
                            padding-top: 5px;
                            box-shadow: -1px 2px 5px 1px rgba(123,125,125,0.15);
                            border-bottom-left-radius: 4px;
                            border-bottom-right-radius: 4px;

                            .search-result {
                                padding: 4px 8px;

                                &:hover {
                                    background: $light-gray-2;
                                    cursor: pointer;
                                }
                            }
                        }
                    }

                    .conversations-list {
                        display: flex;
                        flex-direction: column;
                        flex-grow: 1;
                        height: 0;
                        overflow: auto;
                        padding: 6px;
                        position: relative;
                        text-align: left;

                        .conversation + .blocked-conversations-title {
                            margin-top: 15px;
                        }

                        .blocked-conversations-title {
                            position: relative;
                            display: flex;

                            > * {
                                align-self: center;
                            }

                            .title-text {
                                background: $extra-light-gray;
                                padding: 0 4px;
                            }

                            .title-line {
                                background: $royal-coin-black;
                                height: 1px;
                                flex-grow: 1;
                            }
                        }
                    }
                }
            }

            .chat-room {
                min-width: 220px;
                display: flex;
                flex-direction: column;

                .messages {
                    color: $dark-gray-alt;
                    flex-grow: 1;
                    height: 0;
                    overflow: auto;
                    padding: 15px;
                    text-align: left;

                    .message-wrapper {
                        margin-top: 4px;

                        &:first-of-type {
                            margin-top: 0;
                        }

                        .username {
                            display: inline-block;

                            .label {
                                display: inline-block;
                                font-weight: bold;
                            }
                        }

                        .message {
							margin-left: 3px;
                            display: inline;
                            word-break: break-word;
                            white-space: pre-wrap;
                        }
                    }
                }
            }
        }
    }

    .chat-form {
        padding: 12px;
        position: relative;

        .chat-input {
            background: $extra-light-gray;
            color: $dark-gray;
            font-weight: 300;
            border: 2px solid transparent;
            padding: 14px 70px 14px 12px;
            font-family: 'Avenir', Helvetica, Arial, 'RobotoSlab', sans-serif;
            resize: none;

            &:focus {
                background: white;
                border-color: $teal-alt;
            }
        }

        .submit-btn {
            font-size: .9em;
            min-height: 38px;
            padding: 3px 10px;
            position: absolute;
            right: 26px;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    .username {

        .label {

            &.archer {
                color: $archer;
            }

            &.berserker {
                color: $berserker;
            }

            &.cavalry {
                color: $cavalry;
            }

            &.crossbowman {
                color: $crossbowman;
            }

            &.ensign {
                color: $ensign;
            }

            &.footman {
                color: $footman;
            }

            &.knight {
                color: $knight;
            }

            &.lancer {
                color: $lancer;
            }

            &.light-cavalry {
                color: $light-cavalry;
            }

            &.marshal {
                color: $marshal;
            }

            &.mercenary {
                color: $mercenary;
            }

            &.pikeman {
                color: $pikeman;
            }

            &.royal-guard {
                color: $royal-guard;
            }

            &.scout {
                color: $scout;
            }

            &.swordsman {
                color: $swordsman;
            }

            &.warrior-priest {
                color: $warrior-priest;
            }
        }
    }
}

.direct-messages-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: black;
    opacity: .3;
    z-index: 199;
}

@media only screen and (min-width: $screen-xs) {

    .direct-messages-widget {

        .direct-messages-content {

            .direct-messages-inner {

                .conversations {
                    min-width: 162px;
                }

                .chat-room {
                    min-width: 250px;
                }
            }
        }
    }

}

</style>
